import { create } from "zustand";
import { devtools, persist, createJSONStorage } from "zustand/middleware";
import { NodeEntity } from "@features/search/services/entities/nodeEntity";
import { handleBuildTree } from "@lib/utils/handleBuildTree";
import { ResourceTypeEntity } from "@features/search/services/entities/resourceTypeEntity";
import { ConfigurationEntity, MeetingLabelConfigEntity, MeetingLabelEntity, ParameterEntity } from "../../features/settings/services/entities/ConfigurationEntity";

export interface AppConfigStoreState {
  locations: NodeEntity[];
  locationTree: NodeEntity[];
  categories: NodeEntity[];
  categoryTree: NodeEntity[];
  resourceTypes: ResourceTypeEntity[];
  helpUrlForOutLookAddin: string;
  parameters: ParameterEntity;
  language: string;
  meetingLabel: MeetingLabelConfigEntity | null;
  defaultMeetingLabel: MeetingLabelEntity | null;

  setConfiguration: (configuration: ConfigurationEntity) => void;
  updateCategories: (categories: NodeEntity[]) => void;
  updateLocations: (locations: NodeEntity[]) => void;
  updateResourceTypes: (resourceTypes: ResourceTypeEntity[]) => void;
  updateHelpUrlForOutLookAddin: (helpUrlForOutLookAddin: string) => void;
  updateParameters: (parameters: ParameterEntity) => void;
  updateMeetingLabel: (meetingLabel: MeetingLabelConfigEntity) => void;
  updateDefaultMeetingLabel: (defaultMeetingLabel: MeetingLabelEntity) => void;
  updateLanguage: (language: string) => void;
}

export const useAppConfigStore = create<AppConfigStoreState>()(
  devtools(
    persist(
      (set, get) => ({
        locations: [],
        locationTree: [],
        categories: [],
        categoryTree: [],
        resourceTypes: [],
        helpUrlForOutLookAddin: "",
        parameters: new ParameterEntity(),
        language: "en",
        meetingLabel: null,
        defaultMeetingLabel: null,

        setConfiguration: (configuration: ConfigurationEntity) => {
          const { categories, locations, resourceTypes, helpUrlForOutLookAddin, parameters, parametersFormatted, meetingLabelConfiguration } = configuration;
          if (categories && categories?.length > 0) {
            const categoriesEntities = categories.map((category) => new NodeEntity(category));
            set({ categories: categoriesEntities, categoryTree: handleBuildTree(categoriesEntities) });
          }
          if (locations && locations?.length > 0) {
            const locationsEntities = locations.map((location) => new NodeEntity(location));
            set({ locations: locationsEntities, locationTree: handleBuildTree(locationsEntities) });
          }
          if (resourceTypes && resourceTypes?.length > 0) {
            set({ resourceTypes: resourceTypes });
          }
          if (helpUrlForOutLookAddin) {
            set({ helpUrlForOutLookAddin: helpUrlForOutLookAddin });
          }
          if (parameters && parameters?.length > 0) {
            set({ parameters: parametersFormatted });
          }
          if (meetingLabelConfiguration) {
            set({ meetingLabel: meetingLabelConfiguration, defaultMeetingLabel: meetingLabelConfiguration.meetingLabels.find((m) => m.isDisplayDefault) || null });
          }
        },
        // categories
        updateCategories: (categories: NodeEntity[]) => set({ categories: categories, categoryTree: handleBuildTree(categories) }),
        // locations
        updateLocations: (locations: NodeEntity[]) =>
          set({ locations: locations, locationTree: handleBuildTree(locations) }),
        // resource types
        updateResourceTypes: (resourceTypes: ResourceTypeEntity[]) => set({ resourceTypes: resourceTypes }),
        // help url for outlook addin
        updateHelpUrlForOutLookAddin: (helpUrlForOutLookAddin: string) => set({ helpUrlForOutLookAddin: helpUrlForOutLookAddin }),
        // parameters - now accepts object directly: { ResourceTypeRooms: "Rooms", ... }
        updateParameters: (parameters: ParameterEntity) => set({ parameters: parameters }),
        updateLanguage: (language: string) => set({ language: language }),
        updateMeetingLabel: (meetingLabel: MeetingLabelConfigEntity) => set({ meetingLabel: meetingLabel }),
        updateDefaultMeetingLabel: (defaultMeetingLabel: MeetingLabelEntity) => set({ defaultMeetingLabel: defaultMeetingLabel }),
      }),
      {
        name: "outlook-addin-data-store",
        version: 1,
        storage: createJSONStorage(() =>
          typeof window !== "undefined"
            ? localStorage
            : {
              length: 0,
              clear() { },
              getItem() {
                return null;
              },
              key() {
                return null;
              },
              removeItem() { },
              setItem() { },
            }
        ),
        partialize: (state) => ({}),
      }
    )
  )
);
